import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { DateTime } from 'luxon';
import NProgress from 'nprogress';

import { useCompanyStore } from '../../company/store';
import { authApi } from '@/utilities/axios';
import { showToast } from '@/utilities/toast-handler';
import { formatDisplayDate } from '@/utilities/date-formatter';

const LICENSE_URL = process.env.VUE_APP_API_VERSION + '/licenses';

export const useLicenseStore = defineStore('license', () => {
    const companyStore = useCompanyStore();
    // states
    // here the company_id is set null because, we are gonna set the company id from drop down and select which company we want to give license to
    const license = ref({
        user_id: null, // user_id is for giving licenses to specific users as well but at the moment in the UI, license can be given to companies only
        company_id: null,
        product_id: null,
        start_date: null,
        end_date: null,
        auto_renew: null,
        notified_users: [],
    });
    // activeLicense state to keep track of details
    const activeLicense = ref(null);
    const licenses = ref([]);
    const entityLicenses = ref([]);
    const statuses = ref([
        { label: 'All', value: 'all' },
        { label: 'Only valid', value: 'valid_only' },
        { label: 'Expired only', value: 'expired_only' },
        { label: 'Only future', value: 'future_only' },
    ]);
    // This is used while extracting all the existing licenses from the db
    const params = ref({
        user_id: null,
        company_id: null,
        valid: 'valid', // by default only_valid licenses are returned in the begining
        paginate: true,
        page_size: 10,
    });
    const years = ref([]);
    const months = ref([]);
    const periods = ref([]);
    const loading = ref(false);
    const totalLicences = ref(0);

    // helpers to load the initial values for years, months and periods which will be used while creation of license
    const loadDates = () => {
        // load around 5 years from current time
        let currYear = DateTime.now().year;

        for (let year = currYear; year <= currYear + 10; year++) {
            years.value.push({ label: year, value: year });
        }

        //  load 12 months with labels
        for (let month = 1; month <= 12; month++) {
            const monthName = DateTime.fromObject({ year: currYear, month }).toFormat('LLL');
            months.value.push({ label: monthName, value: month });
        }
        // days will be loaded only after month has been selected

        // load periods
        periods.value = [
            { label: '6 months', value: 6 },
            { label: '12 months', value: 12 },
            { label: '24 months', value: 24 },
            { label: '36 months', value: 36 },
            { label: '48 months', value: 48 },
        ];
    };

    // getters
    const allLicenses = computed(() => {
        return licenses.value?.map((license) => {
            return {
                ...license,
                company_name: companyStore.companies.find((company) => company.id === license.company_id)?.name,
                start_date: formatDisplayDate(license.start_date),
                end_date: formatDisplayDate(license.end_date),
                reminder_date: formatDisplayDate(license.reminder_date),
            };
        });
    });

    // actions
    // this function will generate all the existing valid or invalid licenses based on the params.
    const getAllLicenses = async () => {
        try {
            NProgress.start();
            loading.value = true;

            const response = await authApi.get(`${LICENSE_URL}/all`, {
                params: params.value,
            });
            if (response.statusCode === 200) {
                licenses.value = response.data.data;
                loading.value = false;

                totalLicences.value = response.data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all licenses');
        } finally {
            NProgress.done();
        }
    };

    const createLicense = async (license) => {
        try {
            NProgress.start();
            const response = await authApi.post(LICENSE_URL, license);
            if (response.statusCode === 201) {
                licenses.value.push(response.data);
                showToast(response.toast, 'License created successfully');
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create license');
        } finally {
            NProgress.done();
        }
    };

    const getLicenseDetailsById = async (id) => {
        try {
            NProgress.start();
            // clean the data initially
            activeLicense.value = null;
            // check if its already in the licenses array
            const existingLicense = allLicenses.value.find((license) => license.id === id);
            if (existingLicense) {
                activeLicense.value = existingLicense;
            } else {
                const response = await authApi.get(`${LICENSE_URL}/${id}`);
                if (response.statusCode === 200) {
                    activeLicense.value = response.data;
                }
            }
            return activeLicense.value;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all licenses');
        } finally {
            NProgress.done();
        }
    };

    // this function gets all the valid/invalid licenses that belongs to a particular company or the user based on the params
    const getEntityLicenses = async (params) => {
        try {
            NProgress.start();
            const response = await authApi.get(`${LICENSE_URL}/all`, params);
            if (response.statusCode === 200) {
                entityLicenses.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to get user/company licenses details');
        } finally {
            NProgress.done();
        }
    };

    const renewLicense = async (params) => {
        try {
            NProgress.start();
            const response = await authApi.post(`${LICENSE_URL}/renew`, params);
            if (response.statusCode === 200) {
                showToast(response.toast, 'License renewed successfully');
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to renew license');
        } finally {
            NProgress.done();
        }
    };

    const suspendLicense = async (params) => {
        try {
            NProgress.start();
            const response = await authApi.post(`${LICENSE_URL}/suspend`, params);
            if (response.statusCode === 200) {
                showToast(response.toast, 'License suspended successfully');
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to suspend license');
        } finally {
            NProgress.done();
        }
    };

    return {
        // states
        license,
        activeLicense,
        licenses,
        entityLicenses,
        statuses,
        params,
        years,
        months,
        totalLicences,
        // days,
        periods,
        loading,
        // helpers
        loadDates,
        // getters
        allLicenses,
        // actions
        getAllLicenses,
        createLicense,
        getLicenseDetailsById,
        getEntityLicenses,
        renewLicense,
        suspendLicense,
    };
});
